import * as React from 'react';

import { Head } from '@/components/seo';
import { CookiePolicy } from '@/components/ui/cookie-policy';
import { TokenExpireTimeAlert } from '@/components/ui/modal';
import { cn } from '@/utils/cn';

export type ContentLayoutProps = {
  title: string;
  phase: 'PROD' | 'BETA' | 'ALPHA';
  isLoggedIn: boolean;
  tokenExpireTime: number;
  children: React.ReactNode;
};

// Using the Compound components pattern here
const ContentLayout = ({
  title,
  phase,
  isLoggedIn,
  tokenExpireTime,
  children,
}: ContentLayoutProps) => {
  const childrenArray = React.Children.toArray(children);
  const header = childrenArray.find(
    child => React.isValidElement(child) && child.type === ContentLayout.Header,
  );
  const main = childrenArray.find(
    child => React.isValidElement(child) && child.type === ContentLayout.Main,
  );
  const footer = childrenArray.find(
    child => React.isValidElement(child) && child.type === ContentLayout.Footer,
  );

  return (
    <>
      <Head title={title} />

      <div className="relative min-h-screen">
        {/* Background */}
        <div
          className={cn(['fixed', 'inset-0'])}
          style={{
            backgroundImage:
              "url('/top_structure_img.png'), url('/top_background.png')",
            backgroundSize: 'cover',
          }}
        />

        {/* Overlay */}
        <div className={cn(['bg-white', 'opacity-30'], ['fixed', 'inset-0'])} />

        {/* Main contents */}
        <div className="relative flex min-h-screen flex-col">
          {header}
          <div className="flex flex-1 flex-col overflow-auto">
            <main
              className={cn(
                // Max width
                'container',
                // Centering
                'mx-auto',
                // Padding
                ['px-2', 'py-12'],
                // Expand main content area
                'grow',
              )}
            >
              {main}
            </main>
            {footer}
          </div>
        </div>

        {/* Cookie policy */}
        <CookiePolicy />

        {/* Expire time alert modal window */}
        {/* (display when less than 5 min to expire) */}
        <TokenExpireTimeAlert
          phase={phase}
          isLoggedIn={isLoggedIn}
          tokenExpireTime={tokenExpireTime}
          thresholdMs={1000 * 60 * 5}
        />
      </div>
    </>
  );
};

ContentLayout.displayName = 'ContentLayout';

type ContentLayoutHeaderProps = {
  children: React.ReactNode;
};
const ContentLayoutHeader: React.FC<ContentLayoutHeaderProps> = ({
  children,
}) => children;
ContentLayoutHeader.displayName = 'ContentLayout.Header';

type ContentLayoutMainProps = {
  children: React.ReactNode;
};
const ContentLayoutMain: React.FC<ContentLayoutMainProps> = ({ children }) =>
  children;
ContentLayoutMain.displayName = 'ContentLayout.Main';

type ContentLayoutFooterProps = {
  children: React.ReactNode;
};
const ContentLayoutFooter: React.FC<ContentLayoutFooterProps> = ({
  children,
}) => children;
ContentLayoutFooter.displayName = 'ContentLayout.Footer';

ContentLayout.Header = ContentLayoutHeader;
ContentLayout.Main = ContentLayoutMain;
ContentLayout.Footer = ContentLayoutFooter;

export { ContentLayout };
